import { useMemo, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { JsonData, JsonDataItem } from '../generic-types';
import type { DataGridColumnSettings } from './data-grid-settings.interface';
import type { CheckboxGroupResult } from '../fields/checkbox-group/checkbox-group.component';
import HfCheckboxGroup from '../fields/checkbox-group/hf-checkbox-group.component';
import Button from '../button/button';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import type { Signal } from '@preact/signals-react';
import { useSignalEffect } from '@preact/signals-react';

export interface ColumnVisibilityProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  onColumnsVisibilityChanged: (columns: Array<DataGridColumnSettings>) => void;
  setOverlayVisible?: (value: boolean) => void;
}

export default function ColumnVisibility({
  columns,
  onColumnsVisibilityChanged,
  setOverlayVisible,
}: ColumnVisibilityProps) {
  const [columnData, setColumnData] = useState<JsonData>();
  const { t } = useTranslation();

  const formSchema = yup.object().shape({
    visibleColumns: yup.array().min(1, t('column-validation', 'At least one column must be selected')),
  });

  const { handleSubmit, control, setValue } = useForm<FieldValues>({
    resolver: yupResolver(formSchema) as never,
  });

  useSignalEffect(() => {
    if (columns) {
      setColumnData(
        columns.value.map((column) => {
          return {
            field: column.id,
            title: column.title ?? column.field,
          };
        })
      );

      const visibleColumns: Array<CheckboxGroupResult> = columns.value
        .filter((column) => column.visible)
        .map((column) => {
          return {
            value: column.id,
            text: column.title || column.field,
          };
        });
      setValue('visibleColumns', visibleColumns);
    }
  });

  const onFormSubmit = (data: JsonDataItem) => {
    const visibleColumns = data.visibleColumns as Array<CheckboxGroupResult>;
    const changedColumns: Array<DataGridColumnSettings> = [];
    const allColumns = cloneDeep(columns.value);

    for (let i = 0, length = allColumns.length; i < length; i++) {
      const fieldVisible = visibleColumns.map((item) => item.value).includes(allColumns[i].id);
      if (allColumns[i].visible !== fieldVisible) {
        changedColumns.push({
          ...allColumns[i],
          visible: fieldVisible,
        });
      }
    }

    onColumnsVisibilityChanged(changedColumns);
    setOverlayVisible?.(false);
  };

  const formHeight = useMemo(() => {
    // 235px is the minimum height of the form, as it accomodates 6 rows in it (spec of MYOSH-3071)
    const defaultHeight = 235;
    if (columnData) {
      // If the form has 6 or less columns, apply default height
      if (columnData.length <= 6) {
        return defaultHeight;
      } else {
        // Otherwise, the form should grow by 31px by row element in it
        // The added 40px is to accomodate the "Apply" button
        const computedFormHeight = columnData.length * 31 + 40;

        // If the total computed form height more than 70% of the view's height, it will grow only to 70vh
        // This is also specified in MYOSH-3071
        if (computedFormHeight <= window.innerHeight * 0.7) {
          return computedFormHeight;
        } else {
          return window.innerHeight * 0.7;
        }
      }
    }
    return defaultHeight;
  }, [columnData]);

  return (
    <div className="odin-pt-2">
      <form
        className="odin-flex odin-flex-col odin-justify-between"
        id="columnForm"
        onSubmit={handleSubmit(onFormSubmit)}
        style={{ height: `${formHeight}px` }}
      >
        <div
          className="custom-scroll odin-flex odin-max-w-sm odin-flex-col odin-overflow-x-hidden odin-overflow-y-scroll"
          style={{ maxHeight: '66vh', minHeight: '186px', marginRight: '-1rem', maxWidth: '70vw' }}
        >
          <HfCheckboxGroup
            control={control}
            name="visibleColumns"
            textField="title"
            valueField="field"
            data={columnData}
          />
        </div>
        <div className="odin-flex odin-justify-end">
          <Button htmlType="submit" type="primary">
            Apply
          </Button>
        </div>
      </form>
    </div>
  );
}
